/**
 * La copie pré-corrigée, côté parent (`core-api` §3.28, `web-pwa` §17.9).
 *
 * Un modèle de vision a lu la photo avec le corrigé vérifié sous les yeux et
 * propose un verdict par ligne. **Il propose, il ne note pas** : rien n'entre
 * dans la maîtrise tant qu'un parent n'a pas confirmé, et la confirmation passe
 * par la route qui existait avant — le verdict posé est celui du parent, que le
 * modèle ait eu raison ou non.
 *
 * Ces fonctions sont pures et exportées pour être testées sans monter l'écran.
 */

import type { LigneDEntrainementOut } from "@/api/schema";
import type { Choix } from "@/v2/ChoixDuVerdict";

/** Les verdicts qu'un parent peut poser d'un appui depuis une proposition.
 *  « illisible » n'en est pas un : c'est le modèle qui dit ne pas savoir, et
 *  la ligne revient au parent entière. */
const CONFIRMABLES: ReadonlySet<string> = new Set([
  "juste",
  "moitie",
  "faux",
  "pas_fait",
]);

/** Ce que la proposition dit, dans les mots de l'écran. */
export const PROPOSE: Record<string, string> = {
  juste: "juste",
  moitie: "à moitié juste",
  faux: "faux",
  pas_fait: "pas fait",
  illisible: "illisible — à relire vous-même",
};

/** Le verdict à pré-armer sur une ligne, ou `null` : sans proposition, ou
 *  quand elle est « illisible ». */
export function verdictPropose(ligne: LigneDEntrainementOut): Choix | null {
  const verdict = ligne.proposition?.verdict;
  if (ligne.verdict || !verdict || !CONFIRMABLES.has(verdict)) return null;
  return verdict as Choix;
}

/** Ce que « Tout confirmer » enverra : les lignes pas encore jugées dont la
 *  proposition est confirmable, dans l'ordre de la feuille. Les autres restent
 *  à juger à la main — la liste ne les écarte pas en silence, l'écran dit
 *  combien il en reste. */
export function aConfirmer(
  lignes: readonly LigneDEntrainementOut[],
): { ligneId: string; verdict: Choix }[] {
  return lignes.flatMap((ligne) => {
    const verdict = verdictPropose(ligne);
    return verdict ? [{ ligneId: ligne.ligne_id, verdict }] : [];
  });
}

/** La phrase de confirmation de « Tout confirmer », dans la page. */
export function confirmationDeTout(n: number, restantes: number): string {
  const base = `Confirmer ${n} verdict${n > 1 ? "s" : ""} proposé${n > 1 ? "s" : ""} par le tuteur ? Cela ne se reprend pas.`;
  if (!restantes) return base;
  return `${base} ${restantes} exercice${restantes > 1 ? "s restent" : " reste"} à juger vous-même.`;
}
